import React, { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { AGENT_LINKS } from './links';
import { agentApi, publicApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Plus, Search, User, MapPin, CreditCard, ClipboardCheck, Building2, ShieldCheck, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wizard } from '@/components/Wizard';
import { Loader } from '@/components/Loader';
import { idDocStep } from '@/components/IdDocStep';

const offerLabel = (offer) => `${offer.nom} (${offer.locataires_max} loc. — ${Number(offer.prix_annuel_usd || 0) === 0 ? 'gratuit' : `${offer.prix_annuel_usd} $/an`})`;

function CreateBailleurDialog({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const initialForm = {
    prenom: '', nom: '', email: '', telephone: '',
    adresse: '', ville: '', pays: 'CD', offer: 'decouverte',
    type_piece: '', numero_piece: '',
    photo_piece_recto: null, photo_piece_verso: null,
    consent: false,
  };
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (!open || offers.length) return;
    publicApi.offers()
      .then(({ data }) => setOffers(data.items || []))
      .catch(() => toast.error('Impossible de charger les offres disponibles'));
  }, [open, offers.length]);

  const submit = async (form) => {
    if (!form.consent) {
      toast.error('Le consentement RGPD est requis.');
      throw new Error('consent_required');
    }
    setLoading(true);
    try {
      const { data } = await agentApi.createBailleur({ ...form, consent: true });
      setCreds(data.credentials);
      toast.success('Bailleur ajouté à votre portefeuille');
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
      throw err;
    } finally { setLoading(false); }
  };
  const close = () => { setOpen(false); setCreds(null); };

  const steps = [
    {
      key: 'identity',
      title: t('wizard.bailleur.step1'),
      icon: User,
      validate: (f) => {
        const e = [];
        if (!f.prenom?.trim()) e.push('Prénom requis');
        if (!f.nom?.trim()) e.push('Nom requis');
        if (!f.email?.trim() || !/.+@.+\..+/.test(f.email)) e.push('Email valide requis');
        if (!f.telephone?.trim()) e.push('Téléphone requis');
        return e;
      },
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Prénom *</Label><Input value={f.prenom} onChange={(e) => set('prenom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Nom *</Label><Input value={f.nom} onChange={(e) => set('nom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Téléphone *</Label><Input value={f.telephone} onChange={(e) => set('telephone', e.target.value)} className="mt-1.5" /></div>
        </div>
      ),
    },
    {
      key: 'location',
      title: t('wizard.bailleur.step2'),
      icon: MapPin,
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Adresse</Label><Input value={f.adresse} onChange={(e) => set('adresse', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Ville</Label><Input value={f.ville} onChange={(e) => set('ville', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Pays</Label><Input value={f.pays} onChange={(e) => set('pays', e.target.value)} className="mt-1.5" /></div>
        </div>
      ),
    },
    {
      key: 'subscription',
      title: t('wizard.bailleur.step3'),
      icon: CreditCard,
      render: (f, set) => (
        <div>
          <Label>Offre d'abonnement</Label>
          <Select value={f.offer} onValueChange={(v) => set('offer', v)}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{offers.map((offer) => <SelectItem key={offer.code} value={offer.code}>{offerLabel(offer)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ),
    },
    idDocStep(t, TID),
    {
      key: 'review',
      title: t('wizard.bailleur.step4'),
      icon: ClipboardCheck,
      validate: (f) => {
        const e = [];
        if (!f.consent) e.push('Le consentement RGPD est obligatoire.');
        return e;
      },
      render: (f, set) => (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 space-y-3 text-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Récapitulatif</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><div className="text-xs text-slate-500">Nom complet</div><div className="font-semibold text-[#0D1B2A]">{f.prenom} {f.nom}</div></div>
              <div><div className="text-xs text-slate-500">Email</div><div className="font-semibold text-[#0D1B2A]">{f.email}</div></div>
              <div><div className="text-xs text-slate-500">Téléphone</div><div className="font-semibold text-[#0D1B2A]">{f.telephone}</div></div>
              <div><div className="text-xs text-slate-500">Ville · Pays</div><div className="font-semibold text-[#0D1B2A]">{f.ville || '—'} · {f.pays}</div></div>
              <div className="sm:col-span-2"><div className="text-xs text-slate-500">Offre</div><div className="font-semibold text-[#0D1B2A]">{offers.find((offer) => offer.code === f.offer) ? offerLabel(offers.find((offer) => offer.code === f.offer)) : f.offer}</div></div>
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 cursor-pointer">
            <input
              type="checkbox"
              data-testid="cb-consent-agent"
              checked={!!f.consent}
              onChange={(e) => set('consent', e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#1677FF] shrink-0"
            />
            <div className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Consentement RGPD (obligatoire).</span> Je confirme avoir recueilli le
              consentement du bailleur pour la collecte et le traitement de ses données personnelles
              conformément à la <a href="/politique-confidentialite" target="_blank" rel="noreferrer" className="underline text-[#1677FF]">Politique de confidentialité</a> de LoyerBook.
            </div>
          </label>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : close()}>
      <DialogTrigger asChild>
        <Button data-testid={TID.agentCreateBailleur} className="bg-[#1677FF] hover:bg-[#0E62DB]">
          <Plus className="w-4 h-4 mr-1.5" /> Nouveau bailleur
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#1677FF]" /> {t('wizard.bailleur.title')}</DialogTitle>
          <DialogDescription>Le bailleur sera ajouté à votre portefeuille.</DialogDescription>
        </DialogHeader>
        {!creds ? (
          <Wizard
            steps={steps}
            initialForm={initialForm}
            onSubmit={submit}
            submitLabel={t('wizard.bailleur.submit')}
            loading={loading}
            testidPrefix="agent-cb-wizard"
          />
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Bailleur créé</div>
                <div className="mt-1 text-xs text-emerald-800">Communiquez ces identifiants au bailleur. Il devra changer son mot de passe au premier login.</div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-4 bg-[#0D1B2A] text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60"><User className="w-3 h-3" /> Identifiant</div>
              <div className="text-lg font-bold">{creds.identifier}</div>
              <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60"><KeyRound className="w-3 h-3" /> Mot de passe temporaire</div>
              <div className="text-lg font-bold font-mono">{creds.temp_password}</div>
            </div>
            <DialogFooter><Button onClick={close} className="bg-[#1677FF] hover:bg-[#0E62DB]">Terminer</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AgentBailleurs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await agentApi.listBailleurs(); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((b) => !q || `${b.prenom} ${b.nom} ${b.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout links={AGENT_LINKS} testid={TID.agentSidebar} title="Mes bailleurs">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 h-10" />
        </div>
        <CreateBailleurDialog onCreated={load} />
      </div>

      <div className="lb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {['Bailleur', 'Contact', 'Offre', 'Locataires', 'Expire le'].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-8"><Loader variant="full" /></td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">Aucun bailleur dans votre portefeuille.</td></tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => nav(`/agent/bailleurs/${b.id}`)}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0D1B2A]">{b.prenom} {b.nom}</div>
                    <div className="text-xs text-slate-500">{b.ville} · {b.pays}</div>
                  </td>
                  <td className="px-4 py-3"><div className="text-slate-700">{b.email}</div><div className="text-xs text-slate-500">{b.telephone}</div></td>
                  <td className="px-4 py-3"><Badge className="bg-[#E6F1FF] text-[#1677FF] hover:bg-[#E6F1FF] capitalize">{b.subscription?.offer || '—'}</Badge></td>
                  <td className="px-4 py-3"><span className="font-semibold text-[#0D1B2A]">{b.locataires_count}</span><span className="text-slate-400"> / {b.quota_max}</span></td>
                  <td className="px-4 py-3 text-slate-700">
                    {b.subscription?.expires_at ? new Date(b.subscription.expires_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
